/**
 * Continue with Google on the web (GH-859, DEC-015) — the browser half.
 *
 * ## No Google script on our pages, ever
 *
 * Google Identity Services can mint an ID token only from its own iframe button (which must be
 * loaded *before* the tap — telling Google which organization's page a donor opened, the
 * affiliation inference DESIGN.md self-hosts its fonts to avoid — and which cannot be drawn to
 * match our Apple button) or from One Tap, which Google suppresses after a dismissal and shows
 * nothing to a browser that is not already signed in to Google (most iPhones). So this file uses
 * the protocol GIS itself sits on: Google's OpenID Connect authorization endpoint with
 * `response_type=id_token`, in a window the donor's own tap opens. Nothing is fetched from Google
 * until that tap, and even then nothing of Google's runs in our page.
 *
 * ## The round trip
 *
 *  1. The tap opens a blank window **synchronously** (a popup opened after an `await` is blocked).
 *  2. The server issues a single-use nonce for this door; the window is pointed at Google with it
 *     and a fresh `state`.
 *  3. Google returns to a registered address on our own origin with `#id_token=…&state=…` — a
 *     fragment, so the token never reaches any server log. `relayGoogleCallback` (first thing in
 *     `main.tsx`) hands it back over a same-origin `BroadcastChannel` and closes the window. A
 *     channel rather than `window.opener`: Google's pages may sever the opener on the way through.
 *  4. Only an answer carrying this tap's `state` is taken. The server then verifies signature,
 *     issuer, audience, expiry, `email_verified` and the nonce before a session exists.
 *
 * Nothing here decides anything about identity; the token is forwarded as an opaque string.
 */
import type { DonorSignInOptions } from "@givehere/contracts";

const GOOGLE_AUTHORIZE = "https://accounts.google.com/o/oauth2/v2/auth";
const CHANNEL = "givehere.google-sign-in";
const WINDOW_NAME = "givehere-google";
/** Every state this page mints starts with this, so the relay recognises Google's answer. */
const STATE_PREFIX = "ghg.";
/** As long as the server's nonce lives: after that, no answer could be accepted anyway. */
const ANSWER_TIMEOUT_MS = 10 * 60 * 1000;

/** What travels back from the callback window. Same-origin only, and checked on arrival. */
type GoogleAnswer = {
  readonly state: string;
  readonly idToken: string | null;
  readonly error: string | null;
};

export class GoogleSignInCancelled extends Error {
  constructor() {
    super("Google sign-in was closed.");
    this.name = "GoogleSignInCancelled";
  }
}

/**
 * The return address Google must hand the token to, if one is registered on this page's own
 * origin. Google refuses an unregistered one, so without it the button is not drawn.
 */
export function googleReturnAddress(
  options: DonorSignInOptions | null,
  origin: string = window.location.origin,
): string | null {
  if (!options?.google) return null;
  return options.google.redirectUris.find((uri) => new URL(uri).origin === origin) ?? null;
}

/**
 * Open the window Google will draw in. Must be called inside the tap's own handler, before any
 * `await`: browsers only let a user gesture open a window. Blank and same-origin until pointed.
 */
export function openGoogleWindow(): Window | null {
  return window.open("", WINDOW_NAME, "popup,width=480,height=640");
}

export function googleAuthorizeUrl(input: {
  readonly clientId: string;
  readonly redirectUri: string;
  readonly nonce: string;
  readonly state: string;
}): string {
  const url = new URL(GOOGLE_AUTHORIZE);
  url.search = new URLSearchParams({
    client_id: input.clientId,
    redirect_uri: input.redirectUri,
    response_type: "id_token",
    // `openid email` is the whole ask, as in the app: a name is a field the donor can type.
    scope: "openid email",
    nonce: input.nonce,
    state: input.state,
    // A shared or family device should be asked which account, never handed the last one.
    prompt: "select_account",
  }).toString();
  return url.toString();
}

function isAnswer(value: unknown): value is GoogleAnswer {
  if (typeof value !== "object" || value === null) return false;
  const answer = value as Record<string, unknown>;
  return (
    typeof answer["state"] === "string" &&
    (answer["idToken"] === null || typeof answer["idToken"] === "string") &&
    (answer["error"] === null || typeof answer["error"] === "string")
  );
}

/**
 * Point `popup` at Google and wait for this tap's answer. Resolves with Google's ID token; rejects
 * with `GoogleSignInCancelled` when the donor declines on Google's page, the wait outlives the
 * nonce, or `signal` aborts (a second tap supersedes the first).
 */
export async function authorizeWithGoogle(input: {
  readonly popup: Window;
  readonly clientId: string;
  readonly redirectUri: string;
  readonly nonce: string;
  readonly signal?: AbortSignal;
}): Promise<string> {
  const state = `${STATE_PREFIX}${globalThis.crypto.randomUUID()}`;
  const channel = new BroadcastChannel(CHANNEL);
  try {
    const answered = new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => reject(new GoogleSignInCancelled()), ANSWER_TIMEOUT_MS);
      const settle = (answer: GoogleAnswer) => {
        // Not this tap's answer: an older window, or something else on the channel. Ignored.
        if (answer.state !== state) return;
        clearTimeout(timer);
        if (answer.idToken) resolve(answer.idToken);
        else if (answer.error === "access_denied") reject(new GoogleSignInCancelled());
        else reject(new Error("Google sign-in did not finish."));
      };
      channel.onmessage = (event: MessageEvent) => {
        if (isAnswer(event.data)) settle(event.data);
      };
      input.signal?.addEventListener("abort", () => {
        clearTimeout(timer);
        reject(new GoogleSignInCancelled());
      });
    });
    input.popup.location.href = googleAuthorizeUrl({ ...input, state });
    return await answered;
  } finally {
    channel.close();
  }
}

/**
 * In the window Google returned to: hand the answer back and close. Returns true when this load
 * *was* Google's answer (the page must then render nothing else), false for every ordinary visit.
 *
 * Recognised only by a `state` this page minted, so a stray `#state=` on a shared link does not
 * hijack the page. The fragment is cleared before anything else runs, so the token does not sit
 * in the address bar or the history entry.
 */
export function relayGoogleCallback(win: Window = window): boolean {
  const fragment = new URLSearchParams(win.location.hash.replace(/^#/, ""));
  const state = fragment.get("state");
  if (!state?.startsWith(STATE_PREFIX)) return false;

  const answer: GoogleAnswer = {
    state,
    idToken: fragment.get("id_token"),
    error: fragment.get("error"),
  };
  win.history.replaceState(null, "", win.location.pathname + win.location.search);

  const channel = new BroadcastChannel(CHANNEL);
  channel.postMessage(answer);
  channel.close();
  win.close();
  return true;
}
